import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, TrendingUp, AlertCircle, Target, Search, BarChart3, Wrench } from 'lucide-react';
import OnboardingCard, { OnboardingButton, OnboardingHeading } from '../components/OnboardingCard';

const GOAL_OPTIONS = [
  {
    id: 'raise_score',
    icon: TrendingUp,
    title: 'Raise my credit score',
    description: 'Improve my overall credit health',
  },
  {
    id: 'remove_negatives',
    icon: AlertCircle,
    title: 'Remove inaccurate items',
    description: 'Challenge errors on my report',
  },
  {
    id: 'funding_ready',
    icon: Target,
    title: 'Get ready for funding',
    description: 'Prepare for loans or credit',
  },
  {
    id: 'understand_report',
    icon: Search,
    title: 'Understand my report',
    description: 'Learn what affects my score',
  },
  {
    id: 'track_progress',
    icon: BarChart3,
    title: 'Track my progress',
    description: 'Monitor improvements over time',
  },
  {
    id: 'fix_problems',
    icon: Wrench,
    title: 'Fix old problems',
    description: 'Clean up historical issues',
  },
];

const GoalsStep = ({
  formData,
  updateFormData,
  nextStep,
  onContinue,
  prevStep,
  showBack = true,
  saving = false,
}) => {
  const handleContinue = onContinue || nextStep;
  const selectedGoals = formData.goals || [];
  const canContinue = selectedGoals.length > 0 && !saving;

  const toggleGoal = (goalId) => {
    if (saving) return;

    const isSelected = selectedGoals.includes(goalId);

    if (isSelected) {
      updateFormData({ goals: selectedGoals.filter((g) => g !== goalId) });
    } else if (selectedGoals.length < 3) {
      updateFormData({ goals: [...selectedGoals, goalId] });
    }
  };

  const isSelected = (goalId) => selectedGoals.includes(goalId);

  return (
    <OnboardingCard showGlow={false}>
      <div className="p-8 sm:p-12">
        <OnboardingHeading
          title="What do you want help with first?"
          subtitle="Choose up to 3 so we can guide you the right way."
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          {GOAL_OPTIONS.map((goal, index) => {
            const selected = isSelected(goal.id);
            const Icon = goal.icon;

            return (
              <motion.button
                key={goal.id}
                type="button"
                disabled={saving}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.3 }}
                onClick={() => toggleGoal(goal.id)}
                className={`
                  relative p-4 rounded-xl text-left transition-all duration-300
                  ${saving ? 'opacity-60 cursor-not-allowed' : ''}
                  ${selected
                    ? 'bg-emerald-500/20 border-2 border-emerald-500 shadow-lg shadow-emerald-500/10'
                    : 'bg-slate-700/30 border-2 border-slate-700 hover:border-slate-600 hover:bg-slate-700/50'
                  }
                `}
              >
                {selected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-3 right-3 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"
                  >
                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </motion.div>
                )}

                <div className="flex items-start gap-3">
                  <div
                    className={`
                    w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                    ${selected ? 'bg-emerald-500/30' : 'bg-slate-600/50'}
                  `}
                  >
                    <Icon size={20} className={selected ? 'text-emerald-400' : 'text-slate-400'} />
                  </div>
                  <div className="pr-6">
                    <h3 className={`font-semibold ${selected ? 'text-white' : 'text-slate-200'}`}>
                      {goal.title}
                    </h3>
                    <p className="text-sm text-slate-400 mt-0.5">{goal.description}</p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </motion.div>

        <div className="mt-4 text-center text-sm text-slate-400">
          {selectedGoals.length} of 3 selected
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-6 flex flex-col sm:flex-row gap-3"
        >
          {showBack && (
            <OnboardingButton
              onClick={prevStep}
              variant="ghost"
              disabled={saving}
              className="sm:w-auto order-2 sm:order-1"
            >
              <ArrowLeft size={18} />
              Back
            </OnboardingButton>
          )}

          <OnboardingButton
            onClick={() => handleContinue({ goals: selectedGoals })}
            disabled={!canContinue}
            loading={saving}
            className={`${showBack ? 'flex-1' : 'w-full'} order-1 sm:order-2`}
            data-testid="onboarding-goals-continue"
          >
            Continue
            <ArrowRight size={20} />
          </OnboardingButton>
        </motion.div>
      </div>
    </OnboardingCard>
  );
};

export default GoalsStep;
